import React from 'react';
import NotificationContextInterface, { NotificationAction } from "@app/Interface/NotificationContextInterface";
import { notificationReducer } from "@app/Context/Reducer/NotificationReducer";
import NotificationInterface from "@app/Interface/NotificationInterface";
import NotificationHelper from '@app/Service/NotificationHelper';

const initialNotifications: NotificationInterface[] = [];

const notificationsInitialState: NotificationContextInterface = {
    notifications: initialNotifications
};

const NotificationContext = React.createContext<{
    notificationState: NotificationContextInterface;
    notificationDispatch: React.Dispatch<NotificationAction>;
}>({
    notificationState: notificationsInitialState,
    notificationDispatch: () => null
});

/**
 * Updated to load data from localStorage.
 * see: https://dev.to/bigaru/creating-persistent-synchronized-global-store-using-react-hooks-in-typescript-209a#persistence
 * 
 * @param children
 * @returns 
 */
const NotificationProvider: React.FC = ({ children }) => {

    const STORAGE_KEY = "NOTIFICATION_DATA";

    const [notificationState, notificationDispatch] = React.useReducer(notificationReducer, notificationsInitialState, (state) => {

        // 3rd argument tries to get data from local storage
        const persistedData = localStorage.getItem(STORAGE_KEY);

        const persistedNotification: NotificationInterface[] = persistedData ? JSON.parse(persistedData) : notificationsInitialState.notifications;

        state.notifications = persistedNotification;

        return { ...state };

    });

    // save state on every change
    React.useEffect(() => {

        localStorage.setItem(STORAGE_KEY, JSON.stringify(notificationState.notifications));

    }, [notificationState.notifications]);

    // use the newest data on every localStorage change
    React.useEffect(() => {

        // see: https://developer.mozilla.org/en-US/docs/Web/API/StorageEvent
        window.addEventListener("storage", (event: StorageEvent) => {

            if (event.key === STORAGE_KEY) {

                const persistedData = localStorage.getItem(STORAGE_KEY);

                const newNotificationData: NotificationInterface[] = persistedData ? JSON.parse(persistedData) : null;

                if (newNotificationData) {
                    notificationDispatch({
                        type: "SYNC_NOTIFICATION",
                        payload: newNotificationData
                    });
                }

            }

        });

    }, []);

    return (
        <NotificationContext.Provider value={{ notificationState: notificationState, notificationDispatch: notificationDispatch }}>
            {children}
        </NotificationContext.Provider>
    );

};

/**
 * Marks the notification as dismissed. 
 * Updates context and sends request to update the notification
 * using the middleware 
 * 
 * @param notificationDispatch 
 * @param notification 
 */
const DismissNotification = (notificationDispatch: React.Dispatch<NotificationAction>, notification: NotificationInterface): void => {

    notification.dismissed = true;

    notificationDispatch({
        type: "DISMISS",
        payload: notification
    });  

    // update notification, no reason to wait for it return a response. 
    NotificationHelper.dismissNotification(notification.id);

};


export { NotificationProvider, NotificationContext, DismissNotification };